import React, { useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import { useModalConfirmation, useToast } from "hooks";
import { ACCEPT_TIME_CARD, REOPEN_TIME_CARD, SEND_TIME_CARD_TO_DEEL, EXPORT_TIME_SHEETS } from "graphql/mutations";
import { MODAL_SIZE } from "components/Modal";
import { downloadFileFromBase64, formatCurrency, formatDate } from "utils";
import Icon, { ICON_TYPE } from "components/Icon";
import { TIME_CARD_STATUS } from "constants/index";
import { UserFullName } from "components/User";
import { Row, Col } from "components/Containers";
import { colors } from "style";
import { Spinner } from "components/Loader";
import Tooltip from "components/Tooltip";

/**
 * useTimeCard
 */
export default function useTimeCard() {
  const [showReopenForm, setShowReopenForm] = useState(false);
  const [selected, setSelected] = useState({});
  const { toast } = useToast();
  const { show, hide } = useModalConfirmation();
  const [handleReopen, { loading: reopenTimeCardLoading }] = useMutation(REOPEN_TIME_CARD);
  const [handleAccept, { loading: acceptTimeCardLoading }] = useMutation(ACCEPT_TIME_CARD);
  const [handleSendToDeel, { loading: sendToDeelLoading }] = useMutation(SEND_TIME_CARD_TO_DEEL);
  const [handleExport, { loading: exportTimeCardLoading }] = useMutation(EXPORT_TIME_SHEETS);

  /**
   * Get advanced options for timeCard
   *
   * @param {object} data
   * @param {boolean} isBatch
   * @param {array} listData
   * @param {array} selection
   * @param {boolean} disabled
   * @param {number} month
   * @param {number} year
   * @param {boolean} teamFilterExists
   * @param {object} filters - Filter object containing year and month
   *
   * @returns {array}
   */
  const getAdvancedOptions = ({ data, isBatch, listData, selection, month, year, filters }) => {
    const options = [];
    const status = data?.timeCard?.status;
    const deelLastSyncAt = data?.timeCard?.deelLastSyncAt;
    const timeCardId = data?.timeCard?.id;
    const organizationsFiltered = filters?.organizationIds?.length;

    /**
     * Sets timeCard to "Accepted"
     */
    const acceptOption = {
      label: <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveButton"} />,
      icon: ICON_TYPE.checkmark,
      onClick: () => handleAcceptTimeCard(timeCardId, selection, listData, isBatch),
    };

    /**
     * Sets timeCard to "Accepted" and then to "Sent to Deel"
     */
    const deelAcceptOption = {
      label: <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.DeelApproveButton"} />,
      icon: ICON_TYPE.dollarBill,
      disabled: !data?.profile?.deelContractUrl,
      onClick: () => handleConfirmationModal(timeCardId, month, year, data),
    };

    /**
     * Opens contract overview page on Deel
     */
    const openDeelContract = {
      label: <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.OpenDeelContract"} />,
      icon: ICON_TYPE.share,
      onClick: () => handleDeelNavigation(data?.profile?.deelContractUrl),
    };

    /**
     * Sets timeCard to "Open"
     */
    const reopenOption = {
      label: <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ReopenButton"} />,
      icon: ICON_TYPE.rotate,
      onClick: () => {
        setShowReopenForm(true);
        setSelected(data);
      },
    };

    /**
     * Export time sheets as CSV or ZIP
     */
    const exportOption = {
      label: organizationsFiltered ? (
        <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ExportButton"} />
      ) : (
        <Tooltip tooltipId={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ExportButton.Tooltip"}>
          <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ExportButton"} />
        </Tooltip>
      ),
      icon: ICON_TYPE.download,
      disabled: !organizationsFiltered,
      onClick: () => handleExportTimeSheets(timeCardId, selection, isBatch, filters, data, listData),
    };

    if (status === TIME_CARD_STATUS.review || isBatch) {
      options.push(acceptOption);
    }

    if (status === TIME_CARD_STATUS.review) {
      options.push(reopenOption);
    }

    if (status === TIME_CARD_STATUS.accepted && !isBatch) {
      options.push(reopenOption, deelLastSyncAt ? openDeelContract : deelAcceptOption);
    }

    options.push(exportOption);

    return options;
  };

  const handleConfirmationModal = async (id, month, year, data) => {
    const values = {
      name: <b>{`${data?.profile?.firstName} ${data?.profile?.lastName}`}</b>,
      count: <b>{`${data?.breakdown?.length}`}</b>,
      timespan: <b>{`${formatDate(data?.lastActivityDate, "MMM, YYYY")}`}</b>,
    };

    const response = await show(
      <FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.DeelTaskConfirmation"} />,
      <>
        <FormattedMessage id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.DeelConfirmation" values={values} />
        <div css={styles.line_items_container}>
          {data.breakdown.map((breakdown) => (
            <>
              <div>
                <FormattedMessage
                  key={breakdown.assignmentId}
                  id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.DeelConfirmationLineItem"
                  values={{
                    team: <b>{breakdown?.team?.name}</b>,
                    hours: <b>{breakdown?.timeLoggedFormatted}</b>,
                    rate: <b>{formatCurrency(breakdown?.rate, breakdown?.currency)}</b>,
                    total: (
                      <b>
                        {formatCurrency((breakdown?.timeLoggedMinutes / 60) * breakdown?.rate, breakdown?.currency)}
                      </b>
                    ),
                  }}
                />
              </div>
            </>
          ))}
        </div>
      </>,
      {
        showCancel: true,
        showSave: true,
        primaryButtonMessageId: "Global.Yes",
      }
    );

    if (response) {
      handleSendTimeCardToDeel(id, month, year);
    }
  };

  const handleSendTimeCardToDeel = (id) => {
    handleSendToDeel({
      variables: {
        input: {
          id: id,
        },
      },
      onCompleted: () => {
        toast.success(<FormattedMessage id={"TimeCard.TimeCardsTable.TimeCardAdvanceOptions.Deel.Success"} />);
      },
      onError: () => {
        toast.error(<FormattedMessage id={"TimeCard.TimeCardsTable.TimeCardAdvanceOptions.Deel.Error"} />);
      },
    });
  };

  const handleAcceptTimeCard = (id, selection, listData, isBatch) => {
    handleAccept({
      variables: {
        input: {
          ids: isBatch ? selection : [id],
        },
      },
      onCompleted: (result) => {
        const { accepted, failedToAccept } = result.acceptTimeCard;

        if (isBatch) {
          const failed = failedToAccept?.map((item) => item.id);
          const acc = accepted?.map((item) => item.id);
          const failedTimeSheets = listData?.filter((item) => failed.indexOf(item.id) !== -1);
          const successTimeSheets = listData?.filter((item) => acc.indexOf(item.id) !== -1);

          handleApproveBatch(failedTimeSheets, successTimeSheets);
        } else if (accepted.length > 0) {
          toast.success(<FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.Success"} />);
        } else {
          toast.error(<FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveError"} />);
        }
      },
      onError: () => {
        toast.error(<FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.Error"} />);
      },
    });
  };

  const handleApproveBatch = async (failedTimeSheets, successTimeSheets) => {
    await show(
      <FormattedMessage id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveBatchResultTitle" />,
      <Col gap="0.5rem">
        <div>
          <b>
            <FormattedMessage id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveBatchResultSubtitle" />
          </b>
        </div>
        {successTimeSheets.length > 0 && (
          <>
            <p>
              <FormattedMessage id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveBatchResultSuccess" />
            </p>
            {successTimeSheets.map((item) => (
              <Row key={item.id}>
                <Icon type={ICON_TYPE.checkFilled} color={colors.green} />
                <UserFullName data={item.profile} />
              </Row>
            ))}
          </>
        )}
        {failedTimeSheets.length > 0 && (
          <>
            <p>
              <FormattedMessage id="TimeCard.TimeCardTable.TimeCardAdvanceOptions.ApproveBatchResultFail" />
            </p>
            {failedTimeSheets.map((item) => (
              <Row key={item.id}>
                <Icon type={ICON_TYPE.unavailable} color={colors.red} />
                <UserFullName data={item.profile} />
              </Row>
            ))}
          </>
        )}
      </Col>,
      {
        showCancel: true,
        showSave: false,
        secondaryButtonMessageId: "Global.Close",
        size: MODAL_SIZE.small,
      }
    );
  };

  const handleReopenTimeCard = (id, note) => {
    handleReopen({
      variables: {
        input: {
          id,
          notes: note,
        },
      },
      onCompleted: () => {
        toast.success(
          <FormattedMessage
            id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.Reopen.Success"}
            values={{
              timeCardCount: 1,
            }}
          />
        );
      },
      onError: () => {
        toast.error(<FormattedMessage id={"TimeCard.TimeCardTable.TimeCardAdvanceOptions.Reopen.Error"} />);
      },
    });
  };

  const handleDeelNavigation = (url) => window.open(url, "_blank");

  /**
   * Handle export time sheets
   */
  const handleExportTimeSheets = async (timeCardId, selection, isBatch, filters, data, listData) => {
    const timeCardIds = isBatch ? selection : [timeCardId];

    // Format month as YYYY-MM
    const monthPeriod = `${filters.year}-${String(filters.month).padStart(2, "0")}`;

    // Collect assignment IDs from breakdown data
    let assignmentIds = [];
    if (isBatch && listData) {
      // For batch export: collect all assignment IDs from selected timecards
      assignmentIds = listData
        .filter((item) => selection.includes(item.timeCard?.id))
        .flatMap((item) => item.breakdown?.map((b) => b.assignmentId) || []);
    } else if (data?.breakdown) {
      // For individual export: collect assignment IDs from this timecard
      assignmentIds = data.breakdown.map((b) => b.assignmentId);
    }

    // Calculate accurate file count based on assignments (breakdown)
    const fileCount = assignmentIds.length || 1;

    // Show loading modal
    show(
      <FormattedMessage id="TimeCard.Export.Loading.Title" />,
      <Loader>
        <Spinner size="4rem" />
        <FormattedMessage id="TimeCard.Export.Loading.Message" values={{ filesCount: fileCount }} />
      </Loader>,
      {
        showCancel: false,
        showSave: false,
        size: MODAL_SIZE.small,
      }
    );

    handleExport({
      variables: {
        input: {
          timeCardIds,
          month: monthPeriod,
          assignmentIds: assignmentIds,
        },
      },
      onCompleted: ({ exportTimeSheets }) => {
        hide(); // Close the modal
        downloadFileFromBase64(exportTimeSheets.fileData, exportTimeSheets.filename, exportTimeSheets.contentType);
        toast.success(<FormattedMessage id={"TimeCard.Export.Success"} />);
      },
      onError: () => {
        hide(); // Close the modal on error too
        toast.error(<FormattedMessage id={"TimeCard.Export.Error"} />);
      },
    });
  };

  return {
    getAdvancedOptions,
    showReopenForm,
    setShowReopenForm,
    acceptTimeCardLoading,
    reopenTimeCardLoading,
    sendToDeelLoading,
    exportTimeCardLoading,
    handleReopenTimeCard,
    handleExportTimeSheets,
    selected,
  };
}

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 2rem;
  padding-bottom: 0;
  text-align: center;
`;

const styles = {
  line_items_container: css`
    margin-top: 2rem;
  `,
};
